import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { JobExecStatus } from '../../../../shared/constants';
import { SchedJobsService } from 'src/app/components/ss-sched/services/jobs.service';
import { Router } from '@angular/router';
import { ActiveScheduledJob, ScheduledJobType } from '../interfaces/scheduled-job.interface';

const PendingStep = {
  icon: 'file-ascx',
  label: 'Pending',
};
const RunningStep = {
  icon: 'clock-arrow-rotate',
  label: 'Running',
};
const FinishedStep = {
  icon: 'check',
  label: 'Finished',
};
const FailedStep = {
  icon: 'close',
  label: 'Failed',
  hidden: true
};

@Component({
  selector: 'prism-scheduled-job-modal',
  templateUrl: './scheduled-job-modal.component.html',
  styleUrls: ['./scheduled-job-modal.component.scss'],
})
export class ScheduledJobModalComponent implements OnInit {
  @Input() set job(value: ActiveScheduledJob | null) {
    this.scheduledJob = value;
    this.updateSteps();
  };
  get job(): ActiveScheduledJob | null {
    return this.scheduledJob;
  }
  @Input() jobType: ScheduledJobType = ScheduledJobType.Job;
  @Input() modalTitle: string = 'Job Status';
  @Input() modalMessage: string = 'Your job is being processed.';
  @Input() modalSubTitle: string = 'This process may take a few moments. Results will be available upon completion.';
  @Input() opened: boolean = false;
  @Input() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';

  @Output() closed = new EventEmitter<{ refresh: boolean }>();

  private scheduledJob: ActiveScheduledJob | null = null;

  public steps: Array<{ icon: string; label: string }> = [
    PendingStep,
    RunningStep,
    FinishedStep,
  ];

  public get modalSizeClass(): string {
    return `modal__${this.size}`;
  }

  public get isJobFailed(): boolean {
    return this.job?.execStatus?.toUpperCase() === JobExecStatus.Failed;
  }

  public get isJobPending(): boolean {
    return this.job?.execStatus?.toUpperCase() === JobExecStatus.Pending;
  }

  public get isJobRunning(): boolean {
    return this.job?.execStatus?.toUpperCase() === JobExecStatus.Running;
  }

  public get isJobSuccess(): boolean {
    return this.job?.execStatus?.toUpperCase() === JobExecStatus.Success;
  }

  public get jobCompletionStatus(): string {
    return this.isJobFailed || this.isJobSuccess ? 'Complete' : 'Incomplete';
  }

  public get jobId(): string {
    return this.job?.jobId || '';
  }

  public currentStep: number = 0;

  constructor(
    private readonly jobsService: SchedJobsService,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.updateSteps();
  }

  private updateSteps() {
    this.steps = [PendingStep, RunningStep, FinishedStep];

    if (this.isJobRunning) {
      this.currentStep = 1;
    } else if (this.isJobSuccess) {
      this.currentStep = 2;
    } else if (this.isJobFailed) {
      this.steps.splice(2, 1, FailedStep);
      this.currentStep = 2;
    }
  }

  public cancelJob(): void {
    if (!this.jobId || !this.isJobPending) {
      return;
    }

    this.jobsService.deleteJob(this.jobId).subscribe({
      next: () => {
        this.handleClose();
      },
      error: (err) => {
        console.error('[ScheduledJobModalComponent] Failed to cancel job:', err);
      }
    });
  }

  public navigateToScheduledJobs(): void {
    this.handleClose();
    this.router.navigate(['/admin/scheduler/jobs/list'], { queryParams: { wildcard: this.jobId } });
  }

  public handleClose(): void {
    this.closed.emit({ refresh: this.job?.options?.refresh ?? false });
  }
}
